import React, { useEffect, useState } from 'react';
import styles from './NoListingFoundSingleCard.module.css';
import { FormattedMessage, injectIntl } from '../../../../../util/reactIntl';
import testimonialImage from '../../../../../assets/testimonial1.jpg';
import testimonialImage2 from '../../../../../assets/testimonial2.jpg';
import testimonialImage3 from '../../../../../assets/testimonial3.jpg';
import testimonialImage4 from '../../../../../assets/testimonial4.jpg';
import { IconCollection, NamedLink } from '../../../../../components';

const reviews = [
  {
    name: <FormattedMessage id="AlgoliaSearchPage.reviews1.name" />,
    review: <FormattedMessage id="AlgoliaSearchPage.reviews1.review" />,
    rating: 5,
    image: testimonialImage
  },
  {
    name: <FormattedMessage id="AlgoliaSearchPage.reviews2.name" />,
    review: <FormattedMessage id="AlgoliaSearchPage.reviews2.review" />,
    rating: 5,
    image: testimonialImage2
  },
  {
    name: <FormattedMessage id="AlgoliaSearchPage.reviews3.name" />,
    review: <FormattedMessage id="AlgoliaSearchPage.reviews3.review" />,
    rating: 5,
    image: testimonialImage3
  },
  {
    name: <FormattedMessage id="AlgoliaSearchPage.reviews4.name" />,
    review: <FormattedMessage id="AlgoliaSearchPage.reviews4.review" />,
    rating: 5,
    image: testimonialImage4
  },
]

// SVG Icons
const LocationIcon = () => (
  <svg width="24" height="30" viewBox="0 0 24 30" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.7785 19.0826C22.2436 15.4254 22.1839 9.65123 18.5996 6.06696C14.9548 2.42207 9.04523 2.42207 5.40033 6.06696C1.81607 9.65123 1.75645 15.4254 5.22149 19.0826C6.63684 16.8304 9.1436 15.3333 12 15.3333C14.8564 15.3333 17.3632 16.8304 18.7785 19.0826ZM7.18047 21.0464L12 25.866L16.8196 21.0464C15.9633 19.2452 14.1271 18 12 18C9.87293 18 8.03667 19.2452 7.18047 21.0464ZM12 29.6372L3.51472 21.152C-1.17157 16.4656 -1.17157 8.86764 3.51472 4.18135C8.20101 -0.504947 15.7989 -0.504947 20.4853 4.18135C25.1716 8.86764 25.1716 16.4656 20.4853 21.152L12 29.6372ZM12 11.3333C12.7364 11.3333 13.3333 10.7363 13.3333 9.99996C13.3333 9.26359 12.7364 8.66663 12 8.66663C11.2636 8.66663 10.6667 9.26359 10.6667 9.99996C10.6667 10.7363 11.2636 11.3333 12 11.3333ZM12 14C9.7908 14 8 12.2092 8 9.99996C8 7.79083 9.7908 5.99996 12 5.99996C14.2092 5.99996 16 7.79083 16 9.99996C16 12.2092 14.2092 14 12 14Z" fill="black" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.1287 5.24992L5.1057 1.22692L6.16635 0.16626L12 5.99992L6.16635 11.8335L5.1057 10.7729L9.1287 6.74992H0V5.24992H9.1287Z" fill="#F2704E" />
  </svg>
);

const LeafIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2C6.48 2 2 6.48 2 12c0 
    5.52 4.48 10 10 10 4.55 0 8.41-3.04 
    9.58-7.1.23-.75-.33-1.52-1.11-1.65A7.02 7.02 
    0 0 0 12 5c-.55 0-1 .45-1 
    1v6h-4c-.55 0-1 .45-1 1s.45 1 
    1 1h5c.55 0 1-.45 1-1V7.83c2.94.49 
    5.17 3.05 5.17 6.17 0 3.42-2.75 6.17-6.17 
    6.17S5.83 17.42 5.83 14H4c0 4.41 
    3.59 8 8 8s8-3.59 8-8-3.59-8-8-8z" />
  </svg>
);

const NoListingFoundSingleCard = ({ keyword, selectedArea, isMapExploring, intl }) => {
  const [swiperComponents, setSwiperComponents] = useState(null);
  const [swiperModules, setSwiperModules] = useState(null);

  // Dynamically load Swiper components to avoid SSR issues with ES module
  useEffect(() => {
    let isMounted = true;

    const loadSwiper = async () => {
      try {
        const [{ Swiper, SwiperSlide }, { Pagination, Autoplay }] = await Promise.all([
          import(/* webpackChunkName: "swiper-react" */ 'swiper/react'),
          import(/* webpackChunkName: "swiper-modules" */ 'swiper/modules'),
        ]);

        if (isMounted) {
          setSwiperComponents({ Swiper, SwiperSlide });
          setSwiperModules({ Pagination, Autoplay });
        }
      } catch (error) {
        console.error('Failed to load Swiper:', error);
      }
    };

    loadSwiper();

    return () => {
      isMounted = false;
    };
  }, []);

  const displayArea = isMapExploring
    ? intl.formatMessage({ id: "AlgoliaSearchSection.mapArea" }, { defaultMessage: "map area" })
    : selectedArea;

  const swiperReady = swiperComponents && swiperModules;

  return (
    <div className={styles.card}>
      {/* Title */}
      <h2 className={styles.title}>
        <FormattedMessage id="NoListingsFoundSingleCard.titlePart1" />
        <span className={styles.highlight}>
          &nbsp;{keyword
            ? <FormattedMessage id='NoListingsFoundSingleCard.highlightWithKeyword' values={{ keyword }} />
            : <FormattedMessage id='NoListingsFoundSingleCard.highlight' />}
        </span>&nbsp;
        <FormattedMessage id="NoListingsFoundSingleCard.titlePart2" />
      </h2>

      {/* Location */}
      <div className={styles.location}>
        <LocationIcon />
        <div className={styles.locationText}>{displayArea}</div>
      </div>

      {/* Subtext */}
      <p className={styles.opportunity}>
        <FormattedMessage id="NoListingsFoundSingleCard.opportunity" />
      </p>

      {/* Buttons */}
      <NamedLink className={styles.ctaPrimary} name="CreateAIListing">
        <FormattedMessage id="NoListingsFound.listItem" />
        <i className='ri ri-magic-line' />
      </NamedLink>
      <a className={styles.ctaSecondary} href="https://www.rentanythingstore.com/page/learn_more">
        <FormattedMessage id="NoListingsFound.learnHow" /> <ArrowIcon />
      </a>

      {/* Join locals */}
      <h3 className={styles.joinTitle}>
        <FormattedMessage id="NoListingsFoundSingleCard.joinPart1" />{" "}
        <span className={styles.highlight}>
          <FormattedMessage id="NoListingsFoundSingleCard.joinHighlight" />
        </span>{" "}
        <FormattedMessage id="NoListingsFoundSingleCard.joinPart2" />
      </h3>

      {/* Review */}

      {swiperReady && (
        <div className={styles.testimonialSlider}>
          {/* User */}
          <swiperComponents.Swiper
            modules={[swiperModules.Pagination, swiperModules.Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            pagination={{
              clickable: true,
              renderBullet: function (index, className) {
                return '<span class="' + className + '"></span>';
              },
            }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            loop={true}
            className={styles.swiperContainer}
          >
            {reviews.map((review, index) => (
              <swiperComponents.SwiperSlide key={index}>
                <p className={styles.review}>
                  "{review.review}"
                </p>
                <div key={index} className={styles.user}>
                  <img src={review.image} alt={review.name} loading="lazy" />
                  <span className={styles.userName}>{review.name}</span>
                </div>
              </swiperComponents.SwiperSlide>
            ))}
          </swiperComponents.Swiper>
        </div>
      )}
      {/* Why Rent */}
      {/* <div className={styles.whyRent}>
        <IconCollection name="leaf" />
        <div className={styles.whyRentText}>
          <strong>
            <FormattedMessage id="NoListingsFoundSingleCard.whyRentTitle" />
          </strong>
          <p>
            <FormattedMessage id="NoListingsFoundSingleCard.whyRentDesc" />
          </p>
        </div>
      </div> */}
    </div>
  );
};

export default injectIntl(NoListingFoundSingleCard);